import {
  actionCardArrivedAt,
  getActionCardPeople,
  type ActionCardPersonOption,
  type ActionCardProvider,
} from '@littlebird/action-cards';
import {
  FEED_TYPE_LABELS,
  FeedTabsStore,
  selectFeedItems,
  useUserConfig,
  type FeedConfiguration,
  type FeedItemMetadata,
  type FeedType,
} from '@littlebird/sdk';
import { useEffect, useMemo } from 'react';

import { isWorkingEntry } from '@/features/home/need-you-cards';
import type { WaitingForYouState } from '@/features/home/use-waiting-for-you';
import { tabStore } from '@/features/tabs/tabs.store';
import { userStore } from '@/stores/user.store';

import { getFeedSearchFields, type FeedItem } from './feed-search';
import { splitSubtaskRows, type FeedEntry } from './feed-subtask-rows';
import { feedTypeOfCard } from './feed-type';

export type FeedRow = { item: FeedItem; metadata: FeedItemMetadata };

export type FeedFilterKind = 'apps' | 'types' | 'people';

export type FeedFilters = { query: string } & Record<
  FeedFilterKind,
  readonly string[]
>;

export const EMPTY_FEED_FILTERS: FeedFilters = {
  query: '',
  apps: [],
  types: [],
  people: [],
};

export type FeedFacet = {
  id: string;
  label: string;
  count: number;
  avatarUrl?: string | null;
};

export type FeedFacets = Record<FeedFilterKind, FeedFacet[]>;

const isFeedType = (id: string): id is FeedType => id in FEED_TYPE_LABELS;

const byLabel = (left: FeedFacet, right: FeedFacet) =>
  left.label.localeCompare(right.label) || left.id.localeCompare(right.id);

const USER_PERSON: ActionCardPersonOption = { id: 'user:self', label: 'You' };

const feedTypeOf = (item: FeedItem): FeedType =>
  item.kind === 'chat_prompt' ? 'chat_prompt' : feedTypeOfCard(item.entry);

// A view that narrows the Feed by more than status shows each matching card on its own row, so a subtask that
// matches is not hidden under a parent that does not
const narrowsFeed = (
  query: string,
  { types, apps }: Pick<FeedConfiguration, 'types' | 'apps'>,
  people: readonly string[]
): boolean =>
  query !== '' || types.length > 0 || apps.length > 0 || people.length > 0;

export const useFeedTabs = (
  home: WaitingForYouState,
  filters: FeedFilters = EMPTY_FEED_FILTERS
) => {
  const userConfig = useUserConfig();
  const userId = userStore.userId ?? 'signed-out';
  const appTab = tabStore.activeTab;
  const store = useMemo(() => {
    const selection = appTab
      ? {
          selectedId: appTab.feedViewId,
          onSelect: (id: string) => tabStore.setFeedViewId(appTab.id, id),
        }
      : undefined;

    return new FeedTabsStore(
      userConfig.feed,
      localStorage,
      `feed:last-tab:${userId}`,
      undefined,
      selection
    );
  }, [userConfig, userId, appTab]);

  useEffect(() => {
    // Capture this tab's view before a later default change can move it
    if (appTab && appTab.feedViewId === undefined) {
      tabStore.setFeedViewId(appTab.id, store.activeTab.id);
    }
  }, [appTab, store]);

  return {
    store,
    ...useFeedItems(
      home,
      filters,
      {
        ...store.activeTab.configuration,
        statuses:
          store.activeTab.builtin === 'for_you'
            ? ['waiting', 'in_progress']
            : store.activeTab.configuration.statuses,
      },
      store.getPeople(store.activeTab.id)
    ),
  };
};

export const useFeedItems = (
  home: WaitingForYouState,
  filters: FeedFilters,
  configuration: FeedConfiguration,
  savedPeople: readonly string[] = []
) => {
  const entries: FeedEntry[] = [
    ...home.listItems.map((item) => ({ item, status: 'waiting' as const })),
    ...home.followUp.entries.map((entry) => ({
      item: { kind: 'provider' as const, id: entry.card.id, entry },
      status: 'in_progress' as const,
    })),
    ...home.snoozed.map((entry) => ({
      item: { kind: 'provider' as const, id: entry.card.id, entry },
      status: 'waiting' as const,
      snoozed: true,
    })),
  ];

  const apps = new Map<string, FeedFacet>();
  const types = new Map<string, FeedFacet>();
  const people = new Map<string, FeedFacet>();
  const count = (
    facets: Map<string, FeedFacet>,
    facet: Omit<FeedFacet, 'count'>
  ) => {
    const existing = facets.get(facet.id);
    if (existing) existing.count++;
    else facets.set(facet.id, { ...facet, count: 1 });
  };

  // Counts the card's facets, so call it once per card
  const toRow = ({ item, status, snoozed = false }: FeedEntry): FeedRow => {
    const provider: ActionCardProvider =
      item.kind === 'chat_prompt'
        ? { id: 'Littlebird', label: 'Littlebird' }
        : item.entry.decoded.provider;
    const type = feedTypeOf(item);
    const participants =
      item.kind === 'chat_prompt'
        ? []
        : item.entry.card.origin === 'user'
          ? [USER_PERSON]
          : getActionCardPeople(item.entry.decoded.content, provider);
    count(apps, provider);
    count(types, { id: type, label: FEED_TYPE_LABELS[type] });
    for (const person of participants) count(people, person);

    return {
      item,
      metadata: {
        id: item.id,
        status,
        type,
        app: provider.id,
        people: participants.map((person) => person.id),
        source: home.getProvenance(item.entry.card).sourceLabel,
        timestamp: actionCardArrivedAt(item.entry.card),
        pinned: !!item.entry.card.pinnedAt,
        working: item.kind === 'provider' && isWorkingEntry(item.entry),
        snoozed,
      },
    };
  };
  // Every card on its own row, subtasks too, so facets count them and a filter or search can find them
  const families = entries.map((entry) => {
    const { parent, subtasks } = splitSubtaskRows(entry);
    return {
      item: entry.item,
      parent: toRow(parent),
      subtasks: subtasks.map(toRow),
    };
  });
  const flatRows = families.flatMap(({ parent, subtasks }) => [
    parent,
    ...subtasks,
  ]);
  // A parent's row keeps its subtasks, and its metadata is the one counted above
  const rows = families.map(({ item, parent }) => ({
    item,
    metadata: {
      ...parent.metadata,
      working: item.kind === 'provider' && isWorkingEntry(item.entry),
    },
  }));

  const facets: FeedFacets = {
    apps: [...apps.values()].sort(byLabel),
    types: [...types.values()],
    people: [...people.values()].sort(byLabel),
  };
  const scopedIds = (kind: FeedFilterKind, saved: readonly string[]) => {
    const session = filters[kind].filter((id) =>
      facets[kind].some((facet) => facet.id === id)
    );
    return session.length ? session : [...saved];
  };

  const query = filters.query.trim().toLocaleLowerCase();
  const matchesQuery = ({ item, metadata }: FeedRow) =>
    [
      apps.get(metadata.app)?.label ?? '',
      ...metadata.people.map((id) => people.get(id)?.label ?? ''),
      ...getFeedSearchFields(item).map((field) => field.text),
    ].some((text) => text.toLocaleLowerCase().includes(query));

  const scopedTypes = scopedIds('types', configuration.types).filter(
    isFeedType
  );
  const scopedApps = scopedIds('apps', configuration.apps);
  const scopedPeople = scopedIds('people', savedPeople);
  const selection: FeedConfiguration = {
    ...configuration,
    types: scopedTypes,
    apps: scopedApps,
  };
  const candidates = narrowsFeed(query, selection, scopedPeople)
    ? flatRows
    : rows;
  const selected = selectFeedItems(
    query ? candidates.filter(matchesQuery) : candidates,
    selection,
    (row) => row.metadata,
    scopedPeople
  );

  return {
    facets,
    allItems: rows.map(({ item }) => item),
    items: selected.map(({ item }) => item.item),
    groupIds: selected.map(({ group }) => group),
    groups: selected.map(({ group }) => group),
    subgroupIds: selected.map(({ subgroup }) => subgroup),
    subgroups: selected.map(({ subgroup }) => {
      if (subgroup === undefined) return undefined;
      if (configuration.groupBy === 'app') return apps.get(subgroup)?.label;
      if (configuration.groupBy === 'people')
        return people.get(subgroup)?.label;
      return subgroup;
    }),
  };
};
